import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const G = '#d4a843'
const MU = 'rgba(255,255,255,0.35)'
const BR = 'rgba(255,255,255,0.07)'

export default function UniversityFooter() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const year = new Date().getFullYear()

  const cols = [
    {
      heading: t('university.footer.headings.study', 'Study'),
      links: [
        { label: t('university.prep.eyebrow', 'Prep'), path: '/prep' },
        { label: t('university.footer.links.allProgrammes', 'All Programmes'), path: '/enrol' },
        { label: t('university.nav.dashboard', 'Dashboard'), path: '/dashboard' },
        { label: t('university.nav.progress', 'My Progress'), path: '/progress' },
        { label: t('university.nav.certificates', 'Certificates'), path: '/certificates' },
      ],
    },
    {
      heading: t('university.footer.headings.organisations', 'Organisations'),
      links: [
        { label: t('university.footer.links.corporatePlans', 'Corporate Plans'), path: '/corporate' },
        { label: t('university.footer.links.nfpProgramme', 'NFP Programme'), path: '/nfp' },
        { label: t('university.footer.links.corporateAdmin', 'Corporate Admin'), path: '/corporate/admin' },
      ],
    },
    {
      heading: t('university.footer.headings.give', 'Give'),
      links: [
        { label: t('university.footer.links.giftDegree', '🎁 Gift a Degree'), path: '/gift' },
        { label: t('university.footer.links.payForward', '💙 Pay It Forward'), path: '/give' },
        { label: t('university.footer.links.claimGift', 'Claim a Gift'), path: '/redeem' },
        { label: t('university.footer.links.applyScholarship', 'Apply for Scholarship'), path: '/nfp#apply' },
      ],
    },
    {
      heading: t('university.footer.headings.account', 'Account'),
      links: [
        { label: t('university.nav.signIn', 'Sign In'), path: '/signin' },
        { label: t('university.nav.signUp', 'Sign Up'), path: '/signup' },
        { label: t('university.nav.enrol', 'Enrol Now'), path: '/enrol' },
      ],
    },
  ]

  return (
    <footer style={{ background: '#06060c', borderTop: `1px solid ${BR}`, marginTop: 'auto' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '56px 24px 32px' }}>

        {/* Non-accredited notice */}
        <div style={{ background: 'rgba(212,168,67,0.05)', border: '1px solid rgba(212,168,67,0.15)', borderRadius: 12, padding: '14px 20px', marginBottom: 40, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>📋</span>
          <p style={{ fontSize: 12, color: MU, lineHeight: 1.6, margin: 0 }}>
            <strong style={{ color: 'rgba(255,255,255,0.55)' }}>
              {t('university.footer.accreditationNotice', 'Not an accredited institution.')}
            </strong>{' '}
            {t('university.footer.accreditationText', 'Still Waters University is a personal development and lifelong learning platform operated by Still Waters University LLC (Wyoming, USA). Our programmes and certificates are not government-recognised qualifications and cannot be used for formal academic credit.')}{' '}
            <button onClick={() => navigate('/terms#not-accredited')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: G, fontSize: 12, padding: 0, textDecoration: 'underline' }}>
              {t('university.footer.learnMore', 'Learn more')}
            </button>
          </p>
        </div>

        {/* Top row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 40, marginBottom: 48 }}>

          {/* Brand */}
          <div>
            <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${G}, #b8912e)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                🏛️
              </div>
              <div>
                <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, fontSize: 14, color: '#fff', lineHeight: 1.1 }}>{t('university.global.brand', 'Still Waters')}</div>
                <div style={{ fontSize: 8, color: G, fontWeight: 800, letterSpacing: '2px' }}>{t('university.global.brandSuffix', 'UNIVERSITY')}</div>
              </div>
            </button>
            <p style={{ fontSize: 12, color: MU, lineHeight: 1.7, maxWidth: 200, margin: '0 0 12px' }}>
              {t('university.footer.tagline', '44 structured learning programmes across 10 disciplines, taught conversationally by AI.')}
            </p>
            {/* A Wise Sage credit */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 8, padding: '6px 10px' }}>
              <span style={{ fontSize: 13 }}>🌿</span>
              <div>
                <p style={{ fontSize: 9, color: 'rgba(125,211,252,0.7)', fontWeight: 700, letterSpacing: '1.5px', margin: '0 0 1px' }}>
                  {t('university.footer.partOf', 'PART OF')}
                </p>
                <a href="https://awisesage.com" target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#7dd3fc', fontWeight: 700, textDecoration: 'none' }}>
                  A Wise Sage
                </a>
              </div>
            </div>
          </div>

          {/* Link columns */}
          {cols.map(col => (
            <div key={col.heading}>
              <p style={{ fontSize: 10, fontWeight: 700, color: G, letterSpacing: '2px', marginBottom: 14 }}>
                {col.heading.toUpperCase()}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {col.links.map(l => (
                  <button key={l.path} onClick={() => navigate(l.path)} style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: MU, padding: 0, transition: 'color 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
                    onMouseLeave={e => (e.currentTarget.style.color = MU)}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: `1px solid ${BR}`, paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <p style={{ fontSize: 12, color: MU, margin: 0 }}>
            © {year}{' '}
            <strong style={{ color: 'rgba(255,255,255,0.4)' }}>Still Waters University LLC</strong>
            {' · '}
            {t('university.footer.location', 'Wyoming, United States')}
          </p>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {[
              { label: t('university.footer.terms', 'Terms of Service'), path: '/terms' },
              { label: t('university.footer.privacy', 'Privacy Policy'), path: '/privacy' },
            ].map(l => (
              <button key={l.path} onClick={() => navigate(l.path)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: MU, padding: 0, transition: 'color 0.15s', fontFamily: "'Inter', sans-serif" }}
                onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}
                onMouseLeave={e => (e.currentTarget.style.color = MU)}>
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}
